// A thread's requests to an agent and its answers, as a conversation: what
// the panel shows (RelayThread), and what a reply carries so the agent sees
// the turns before it. Each reply is a new task on the thread's topic, its
// payload the thread's as usual with the reply as its `instruction` and the
// earlier turns in its optional `conversation` (api/relayTask.ts).
import type { RelayTask } from '../api/relay';
import type { TaskBody, Turn } from '../api/relayTask';
export type { Turn };
/** The helper's limit on a task's payload, as JSON bytes (relay.send checks it too). */
export const TASK_BYTES = 65536;
/** Room kept under it, for what the payload's own fields may grow by. */
const MARGIN = 2048;
/** The longest a single earlier turn goes as; a longer one is cut with a note. */
export const TURN_MAX = 8000;
export const CUT_NOTE = '\n\n[…cut]';

/** The turns of `tasks` (oldest first): each one's request where this browser recorded it, then its answer if any. */
export function turnsOf(tasks: readonly RelayTask[]): Turn[] {
  return tasks.flatMap((t) => [
    ...(typeof t.asked === 'string' && t.asked.trim() ? [{ from: 'person' as const, text: t.asked }] : []),
    ...(typeof t.result === 'string' && t.result.trim() ? [{ from: 'agent' as const, text: t.result }] : []),
  ]);
}

const bytes = (v: unknown) => new TextEncoder().encode(JSON.stringify(v)).byteLength;
/** `text` cut to `max` UTF-16 units (not inside a surrogate pair), with a note saying so. */
function cut(text: string, max: number): string {
  if (text.length <= max) return text;
  let end = max;
  const c = text.charCodeAt(end - 1);
  if (c >= 0xd800 && c <= 0xdbff) end--;
  return text.slice(0, end) + CUT_NOTE;
}

/**
 * `payload` with the earlier `turns` as its `conversation`, kept under the
 * helper's limit: each turn cut to TURN_MAX, then the oldest dropped until
 * the whole fits. None left (or none given): no `conversation` at all.
 */
export function withConversation<P extends object>(payload: P, turns: readonly Turn[], limit = TASK_BYTES - MARGIN): P & { conversation?: Turn[] } {
  let kept = turns.map((t) => ({ from: t.from, text: cut(t.text, TURN_MAX) }));
  while (kept.length && bytes({ ...payload, conversation: kept }) > limit) kept = kept.slice(1);
  return kept.length ? { ...payload, conversation: kept } : payload;
}

/** `body` with the earlier `turns` as its payload's `conversation` (`withConversation`). */
export function withTurns(body: TaskBody, turns: readonly Turn[]): TaskBody {
  return body.kind === 'changeling.review-range.v1' ? { kind: body.kind, payload: withConversation(body.payload, turns) } : { kind: body.kind, payload: withConversation(body.payload, turns) };
}
